import { type CreateTeamMutation } from "@/graphql/generated/graphql";
import { useMutation } from "@apollo/client";

import { updateCacheWithNewTeam } from "./_utils";
import { createTeamMutationDocument, teamListForWorkspaceQueryDocument } from "./team.gql";
import { EVENT_TEAM_CREATED, trackTeamEvent } from "./tracking";

export type CreatedTeam = CreateTeamMutation["createTeam"];

export interface UseCreateTeamProps {
  workspaceId: string;
  onCompleted?: (createdTeam: CreatedTeam) => void;
  onError?: (e: Error) => void;
}

export const useTeamCreate = ({ workspaceId, onCompleted, onError }: UseCreateTeamProps) => {
  const [createTeam, { loading, error }] = useMutation(createTeamMutationDocument, {
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
    refetchQueries: [
      {
        query: teamListForWorkspaceQueryDocument,
        variables: { input: workspaceId },
      },
    ],
    update: (cache, { data }) => {
      const team = data?.createTeam;
      if (!team) {
        return;
      }
      updateCacheWithNewTeam(cache, team);
      trackTeamEvent(EVENT_TEAM_CREATED, team);
    },
  });

  function onUpdateCompleted(data: CreateTeamMutation) {
    if (onCompleted) {
      onCompleted(data.createTeam);
    }
  }

  function onUpdateError(e: Error) {
    if (onError) {
      onError(e);
    }
  }

  return {
    createTeam,
    loading,
    error,
  };
};
